import { useCallback, useRef, useState } from 'react'
import { ScanBarcode, Search } from 'lucide-react'

import type { WorkflowSearchTracker } from '@/lib/order-workflow-contract'
import type { Product } from '@/lib/api/product-api'
import type { PosProductSearchDropdownHandle } from '@/components/pos/pos-product-search-dropdown'
import { useBarcodeScanner } from '@/hooks/use-barcode-scanner'
import { PosProductSearchDropdown } from '@/components/pos/pos-product-search-dropdown'
import { m } from '@/lib/paraglide/messages'

interface Props {
    workflow?: WorkflowSearchTracker
    /** Owner begins work on real input; background queries never start attempts. */
    onSearchInput?: () => void
    onProductFound: (product: Product, qty: number) => void
    onBarcodeNotFound: (barcode: string) => void
    /** Forward ref to input element for external focus control */
    inputRef?: React.RefObject<HTMLInputElement | null>
    className?: string
    /** topbar = POS transparent style, form = standard input style */
    variant?: 'topbar' | 'form'
    /** Người dùng bấm vào ô (hoặc ↓ khi ô trống) thì hiện luôn danh sách SP để
     *  duyệt, không cần gõ. Focus do code gọi (auto-focus) KHÔNG mở. */
    openOnFocus?: boolean
}

export function BarcodeProductSearch({
    onProductFound,
    onBarcodeNotFound,
    workflow,
    onSearchInput,
    inputRef: externalRef,
    className = '',
    variant = 'form',
    openOnFocus = false,
}: Props) {
    const [search, setSearch] = useState('')
    const [browseOpen, setBrowseOpen] = useState(false)
    const internalRef = useRef<HTMLInputElement>(null)
    const ref = externalRef ?? internalRef

    const stableOnProductFound = useCallback(
        (product: Product, qty: number) => {
            workflow?.setContext({ entryMethod: 'barcode' })
            workflow?.step('product_selected', { entryMethod: 'barcode' })
            setSearch('')
            setBrowseOpen(false)
            onProductFound(product, qty)
        },
        [onProductFound, workflow],
    )

    const stableOnBarcodeNotFound = useCallback(
        (barcode: string) => onBarcodeNotFound(barcode),
        [onBarcodeNotFound],
    )

    const {
        entryMode,
        toggleEntryMode,
        scanQuantity,
        setScanQuantity,
        handleSearchKeyDown,
        scanBarcode,
    } = useBarcodeScanner({
        onProductFound: stableOnProductFound,
        onBarcodeNotFound: stableOnBarcodeNotFound,
    })

    const dropdownRef = useRef<PosProductSearchDropdownHandle>(null)

    const handleSelect = useCallback(
        (product: Product) => {
            onProductFound(product, scanQuantity)
            setSearch('')
            setBrowseOpen(false)
            setScanQuantity(1)
        },
        [onProductFound, scanQuantity, setScanQuantity],
    )

    const isTopbar = variant === 'topbar'

    const inputClass = isTopbar
        ? 'h-11 w-full rounded bg-primary-foreground/15 pl-8 pr-2 text-sm text-primary-foreground placeholder:text-primary-foreground/60 focus:outline-none focus:bg-primary-foreground/25 md:h-9 md:pl-7'
        : 'h-11 w-full rounded-md border bg-background pl-8 pr-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring md:h-9 md:pl-7'

    const iconBaseClass = isTopbar
        ? 'text-primary-foreground/60'
        : 'text-muted-foreground'

    const barcodeActiveClass = isTopbar
        ? 'text-primary-foreground'
        : 'text-primary'

    const barcodeInactiveClass = isTopbar
        ? 'text-primary-foreground/40'
        : 'text-muted-foreground/40'

    const qtyClass = isTopbar
        ? 'h-11 w-16 rounded bg-primary-foreground/15 px-2 text-center text-sm text-primary-foreground placeholder:text-primary-foreground/60 focus:outline-none focus:bg-primary-foreground/25 md:h-9'
        : 'h-11 w-16 rounded-md border bg-background px-2 text-center text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring md:h-9'

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <div className="relative flex-1">
                <Search
                    className={`absolute left-2.5 top-1/2 size-4 -translate-y-1/2 md:left-2 md:size-3.5 ${iconBaseClass}`}
                />
                <input
                    ref={ref}
                    placeholder={m.product_search()}
                    value={search}
                    onChange={(e) => {
                        onSearchInput?.()
                        setSearch(e.target.value)
                    }}
                    onMouseDown={() => {
                        if (openOnFocus) setBrowseOpen(true)
                    }}
                    onKeyDown={(e) => {
                        // Fast-path scanner wins first (timing heuristic)
                        handleSearchKeyDown(e)
                        if (e.defaultPrevented) {
                            setSearch('')
                            return
                        }
                        if (e.key === 'Escape' && browseOpen) {
                            setBrowseOpen(false)
                            return
                        }
                        if (
                            e.key === 'ArrowDown' &&
                            openOnFocus &&
                            !browseOpen &&
                            !e.currentTarget.value.trim()
                        ) {
                            e.preventDefault()
                            setBrowseOpen(true)
                            return
                        }
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            if (
                                dropdownRef.current?.moveSelection(
                                    e.key === 'ArrowDown' ? 1 : -1,
                                )
                            ) {
                                e.preventDefault()
                            }
                            return
                        }
                        if (e.key === 'Enter') {
                            const value = e.currentTarget.value.trim()
                            if (!value) {
                                // Ô trống đang duyệt danh sách: Enter chọn dòng sáng.
                                if (dropdownRef.current?.selectCurrent()) {
                                    e.preventDefault()
                                }
                                return
                            }
                            e.preventDefault()
                            // Prefer dropdown selection when results exist
                            if (dropdownRef.current?.selectCurrent()) return
                            // No dropdown hit → force exact barcode lookup so
                            // not-found opens the quick-create dialog.
                            scanBarcode(value)
                            setSearch('')
                        }
                    }}
                    onBlur={() => {
                        setSearch('')
                        setBrowseOpen(false)
                    }}
                    className={inputClass}
                />
                <PosProductSearchDropdown
                    ref={dropdownRef}
                    workflow={workflow}
                    searchTerm={search}
                    onSelect={handleSelect}
                    browse={openOnFocus && browseOpen}
                />
            </div>

            {entryMode === 'normal' && (
                <input
                    type="number"
                    min={1}
                    max={9999}
                    value={scanQuantity}
                    onChange={(e) =>
                        setScanQuantity(
                            Math.min(
                                9999,
                                Math.max(1, Number(e.target.value) || 1),
                            ),
                        )
                    }
                    placeholder={m.order_quantity()}
                    className={qtyClass}
                />
            )}

            <button
                type="button"
                onClick={toggleEntryMode}
                title={m.pos_scan_barcode()}
                className={`flex size-11 items-center justify-center rounded-md md:size-9 ${entryMode === 'normal' ? barcodeActiveClass : barcodeInactiveClass}`}
            >
                <ScanBarcode className="size-4" />
            </button>
        </div>
    )
}
